import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Badge } from "../ui/badge";
import { LogOut, LayoutDashboard, Sparkles, Search, BadgeCheck, User as UserIcon } from "lucide-react";

export function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const isAuth = !!user && user !== false;

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/75 border-b border-zinc-200">
      <div className="mx-auto max-w-7xl px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
          <div className="h-7 w-7 grid place-items-center rounded-md surface-navy font-display text-lg">B</div>
          <span className="font-display text-xl tracking-tight">BizzAtlas</span>
          <span className="hidden md:inline font-mono-tag text-[10px] text-zinc-400 ml-2 uppercase">/ B2B Directory</span>
        </Link>

        <nav className="hidden md:flex items-center gap-7 text-sm">
          <NavLink to="/categories" data-testid="nav-categories" className={({isActive}) => `link-underline ${isActive ? "text-zinc-900" : "text-zinc-600 hover:text-zinc-900"}`}>Categories</NavLink>
          <NavLink to="/search" data-testid="nav-search" className={({isActive}) => `link-underline ${isActive ? "text-zinc-900" : "text-zinc-600 hover:text-zinc-900"}`}>Explore</NavLink>
          <NavLink to="/pricing" data-testid="nav-pricing" className={({isActive}) => `link-underline ${isActive ? "text-zinc-900" : "text-zinc-600 hover:text-zinc-900"}`}>Pricing</NavLink>
          <NavLink to="/list-business" data-testid="nav-list-biz" className="link-underline text-zinc-600 hover:text-zinc-900">List your business</NavLink>
        </nav>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => nav("/search")}
            className="hidden md:flex items-center gap-2 px-3 h-9 rounded-md border border-zinc-200 text-sm text-zinc-500 hover:border-zinc-300 transition"
            data-testid="quick-search-trigger"
          >
            <Search className="h-4 w-4" />
            Search businesses
            <span className="font-mono-tag text-[10px] text-zinc-400 ml-3">⌘K</span>
          </button>

          {!isAuth && (
            <>
              <Button asChild variant="ghost" size="sm" data-testid="nav-login-btn">
                <Link to="/login">Sign in</Link>
              </Button>
              <Button asChild size="sm" className="btn-brand" data-testid="nav-signup-btn">
                <Link to="/register">Get started</Link>
              </Button>
            </>
          )}

          {isAuth && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild data-testid="user-menu-trigger">
                <button className="flex items-center gap-2 pl-1 pr-2 h-9 rounded-full border border-zinc-200 hover:border-zinc-300">
                  <Avatar className="h-7 w-7"><AvatarFallback className="surface-navy text-xs">{user.name?.[0]?.toUpperCase() || "U"}</AvatarFallback></Avatar>
                  <span className="text-sm hidden sm:block">{user.name}</span>
                  {user.membership !== "free" && (
                    <Badge variant="outline" className="border-blue-200 text-blue-700 text-[10px] uppercase">
                      <Sparkles className="h-3 w-3 mr-1" />{user.membership}
                    </Badge>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-sm">{user.name}</div>
                  <div className="text-xs text-zinc-500 font-normal">{user.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/profile")} data-testid="menu-profile">
                  <UserIcon className="h-4 w-4 mr-2" />Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/dashboard")} data-testid="menu-dashboard">
                  <LayoutDashboard className="h-4 w-4 mr-2" /> Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/dashboard/invoices")} data-testid="menu-invoices">
                  Invoices
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/dashboard/ads")} data-testid="menu-ads">
                  Ad campaigns
                </DropdownMenuItem>
                {(user.role === "admin" || user.role === "manager" || user.role === "editor") && (
                  <DropdownMenuItem onClick={() => nav("/admin")} data-testid="menu-admin">
                    <BadgeCheck className="h-4 w-4 mr-2" />Admin panel
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); nav("/"); }} data-testid="menu-logout">
                  <LogOut className="h-4 w-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </header>
  );
}
